"use client"
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

/**
 * Reusable Glassmorphic Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Function to call when modal closes
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.size - Modal size (sm, md, lg, xl) - defaults to md
 * @param {string} props.footer - Optional footer content
 */
const GlassmorphicModal = ({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  footer
}) => {
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);

    // Prevent body scrolling when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Responsive size classes based on screen size
  const sizeClasses = {
    sm: "w-full sm:w-2/3 md:w-1/2 lg:w-1/3",
    md: "w-full sm:w-3/4 md:w-2/3 lg:w-1/2",
    lg: "w-full sm:w-5/6 md:w-3/4 lg:w-2/3",
    xl: "w-full sm:w-5/6 lg:w-5/6"
  };

  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { opacity: 0 }
  };

  // Different animation variants based on screen size
  const getModalVariants = () => {
    // For mobile devices (slide up from bottom)
    if (typeof window !== 'undefined' && window.innerWidth < 640) {
      return {
        hidden: { y: '100%', opacity: 0.8 },
        visible: {
          y: 0,
          opacity: 1,
          transition: {
            type: "spring",
            damping: 25,
            stiffness: 300,
            duration: 0.4
          }
        },
        exit: {
          y: '100%',
          opacity: 0.8,
          transition: { duration: 0.3 }
        }
      };
    }

    // For tablets and larger (slide from right)
    return {
      hidden: { x: '100%', opacity: 0.8 },
      visible: {
        x: 0,
        opacity: 1,
        transition: {
          type: "spring",
          damping: 25,
          stiffness: 300,
          duration: 0.4
        }
      },
      exit: {
        x: '100%',
        opacity: 0.8,
        transition: { duration: 0.3 }
      }
    };
  };

  // State to track screen size
  const [isMobile, setIsMobile] = React.useState(false);

  // Update animation on window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    // Initial check
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className={`fixed ${isMobile ? 'inset-x-0 bottom-0 rounded-t-xl max-h-[90vh]' : 'top-0 right-0 bottom-0'} ${sizeClasses[size]} bg-white/80 backdrop-blur-md border border-white/20 shadow-xl z-50 flex flex-col`}
            variants={getModalVariants()}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <div className="p-3 sm:p-4 border-b border-gray-200/80 flex justify-between items-center bg-white/40">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 truncate">{title}</h2>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="p-1 rounded-full bg-white/50 hover:bg-white/90 text-gray-700 hover:text-red-500 transition-colors"
                onClick={onClose}
                aria-label="Close modal"
              >
                <X size={20} />
              </motion.button>
            </div>

            {/* Content with improved scrolling */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 overscroll-contain">
              {children}
            </div>

            {/* Footer (optional) */}
            {footer && (
              <div className="p-3 sm:p-4 border-t border-gray-200/80 bg-white/40">
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default GlassmorphicModal;